import { useState } from "react";
import MerakliGuidance from '../components/MerakliGuidance';
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import AssignStudentModal from "@/pages/ogretmen/components/AssignStudentModal";
import { useTeacherStudents, unassignStudent } from "@/hooks/useTeacher";

export default function Ogrencilerim() {
  const { data, loading, error, reload } = useTeacherStudents();
  const [assignOpen, setAssignOpen] = useState(false);
  const [guidance,setGuidance]=useState<{id:string;name:string}|null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const list = data ?? [];
  const assignedUserIds = new Set(list.map((s) => s.userId));

  const handleRemove = async (userId: string) => {
    setRemovingId(userId);
    await unassignStudent(userId);
    setRemovingId(null);
    reload();
  };

  return (
    <div className="space-y-6">
      {guidance&&<MerakliGuidance studentId={guidance.id} name={guidance.name} onClose={()=>setGuidance(null)}/>}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeader
          title="Öğrencilerim"
          description="Sana atanmış öğrenciler ve gelişimleri."
        />
        <button
          type="button"
          onClick={() => setAssignOpen(true)}
          className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
        >
          <i className="ri-user-add-line" aria-hidden="true"></i>
          Öğrenci Ata
        </button>
      </div>

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-group-line"
          title="Henüz öğrenci yok"
          description='"Öğrenci Ata" ile öğrencilerini ekle, gelişimlerini buradan takip et.'
        />
      ) : (
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <ul className="divide-y divide-background-200">
            {list.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-4 py-3.5">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-heading font-semibold text-sm shrink-0">
                  {s.fullName.trim().charAt(0).toLocaleUpperCase("tr-TR")}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-900">{s.fullName}</p>
                  <p className="text-xs text-foreground-500">
                    {s.grade ? `${s.grade} · ` : ""}
                    {s.city ?? "Öğrenci"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(s.userId)}
                  disabled={removingId === s.userId}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium text-foreground-500 whitespace-nowrap hover:bg-background-100 hover:text-accent-700 disabled:opacity-50 transition-colors"
                >
                  {removingId === s.userId ? (
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                  ) : (
                    <i className="ri-user-unfollow-line" aria-hidden="true"></i>
                  )}
                  Kaldır
                </button>
                <button className="text-sm text-primary-700" onClick={()=>setGuidance({id:s.userId,name:s.fullName})}>Meraklı’ya yönlendir</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <PanelLinks
        links={[
          { label: "Öğrenci Analizleri", href: "/ogretmen/ogrenci-analizleri", icon: "ri-line-chart-line" },
          { label: "Ödevler", href: "/ogretmen/odevler", icon: "ri-task-line" },
          { label: "Mesajlar", href: "/ogretmen/mesajlar", icon: "ri-chat-3-line" },
        ]}
      />

      <AssignStudentModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onAssigned={reload}
        assignedUserIds={assignedUserIds}
      />
    </div>
  );
}
